import { toJpeg } from 'html-to-image';
import type { PageElement } from '@erp/rpc';

const MAX_WIDTH = 1024;
const JPEG_QUALITY = 0.7;
const MAX_ELEMENTS = 150;

const INTERACTIVE_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="link"], [data-testid]';

let storedRects = new Map<number, DOMRect>();

export function getStoredRect(elementId: number): DOMRect | undefined {
  return storedRects.get(elementId);
}

function computeLocation(rect: DOMRect): string {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const col = cx < vw / 3 ? 'left' : cx < (2 * vw) / 3 ? 'center' : 'right';
  const row = cy < vh / 3 ? 'top' : cy < (2 * vh) / 3 ? 'middle' : 'bottom';

  if (col === 'center' && row === 'middle') return 'center';
  if (col === 'center') return row;
  if (row === 'middle') return col;
  return `${row}-${col}`;
}

function collectPageElements(): PageElement[] {
  const results: PageElement[] = [];
  const newRects = new Map<number, DOMRect>();
  let nextId = 1;

  const candidates = document.querySelectorAll<Element>(INTERACTIVE_SELECTOR);
  for (const el of candidates) {
    if (results.length >= MAX_ELEMENTS) break;

    const htmlEl = el as HTMLElement;

    if (htmlEl.closest('[data-capture-exclude]')) continue;

    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    if (htmlEl.offsetParent === null && el.tagName !== 'BODY') continue;

    if (
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth
    )
      continue;

    let text = (htmlEl.innerText ?? el.textContent ?? '').trim().slice(0, 80);
    if (!text) {
      text = (
        el.getAttribute('aria-label') ??
        el.getAttribute('data-testid') ??
        ''
      )
        .trim()
        .slice(0, 80);
    }

    const role = el.getAttribute('role') ?? el.tagName.toLowerCase();
    const tag = el.tagName.toLowerCase();
    const location = computeLocation(rect);
    const id = nextId++;

    newRects.set(id, rect);
    results.push({ id, text, role, tag, location });
  }

  storedRects = newRects;
  return results;
}

export async function captureScreen(): Promise<{
  imageData: string;
  mimeType: 'image/jpeg';
  elements: PageElement[];
}> {
  const elements = collectPageElements();

  const pixelRatio = Math.min(
    MAX_WIDTH / window.innerWidth,
    window.devicePixelRatio,
  );

  const dataUrl = await toJpeg(document.body, {
    quality: JPEG_QUALITY,
    pixelRatio,
    skipFonts: true,
    cacheBust: false,
    imagePlaceholder:
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    onImageErrorHandler: () => {
      // Swallow per-image errors so one broken icon doesn't kill the capture
    },
    filter: (node) => {
      if (node.nodeName === 'SCRIPT') return false;
      if (
        node instanceof HTMLElement &&
        node.hasAttribute('data-capture-exclude')
      )
        return false;
      return true;
    },
  });

  const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
  return { imageData: base64, mimeType: 'image/jpeg', elements };
}
